import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CalendarDays,
    AlertTriangle,
    CalendarClock,
    DollarSign,
    FileSpreadsheet,
    FileText,
    ClipboardList,
    FileArchive,
    File,
    Users,
    UserCheck,
    Clock,
    ListChecks,
    List,
    Grid,
    Table,
    Search,
    ChevronRight,
    Building2,
    FileCheck,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useSidebar } from "../../components/ui/sidebar";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard } from "../../components/ui/cards";
import { isOrgOverview, isOrgAdminUser } from "../../utils/branchSession";

const ReportIndex = () => {

    const userPermissions = JSON.parse(sessionStorage.getItem("loginUserPermission"));
    const reportPermissions = userPermissions.find(permission => permission.label === 'reports')
    const isReportingManager = sessionStorage.getItem("reportingManager") === "true";

    // All available reports
    const allReports = [
        { title: "Branch Wise Activity Report", icon: Building2, description: "Payroll runs, fines, LOP, credits and salary activities by branch.", route: "/reports/branch-wise-activity", permissionKey: "branch_wise_activity_report", orgAdminOnly: true },
        { title: "Day-wise Attendance", icon: CalendarClock, description: "View daily attendance of employees.", route: "/reports/daywise-attendance", permissionKey: "day_wise_attendance" },
        { title: "Detailed Attendance", icon: ListChecks, description: "Period report with daily status, exceptions, and correction flags.", route: "/reports/detailed-attendance", permissionKey: "day_wise_attendance" },
        { title: "Department Wise Activity", icon: Users, description: "Department-level activity feed: remarks, corrections, leave, and profile changes.", route: "/reports/department-wise-activity", permissionKey: "day_wise_attendance" },
        { title: "Discrepancy Report", icon: AlertTriangle, description: "Attendance, payroll, master data, and salary correction mismatches.", route: "/reports/discrepancy-report", permissionKey: "day_wise_attendance" },
        { title: "Permission Punch Report", icon: Clock, description: "Permission leave windows vs actual punch times and alerts.", route: "/reports/permission-punch-report", permissionKey: "day_wise_attendance" },
        { title: "Month Wise Attendance", icon: CalendarDays, description: "Monthly attendance summary of all employees.", route: "/reports/monthwise-attendance", permissionKey: "month_wise_attendance" },
        { title: "Salary Process Bank", icon: DollarSign, description: "Details of salary processing and bank transfers.", route: "/reports/salary-process-bank", permissionKey: "salary_process_bank" },
        { title: "Employee Salary Summary", icon: FileSpreadsheet, description: "Summary of employee salaries for the period.", route: "/reports/employee-salary-summary", permissionKey: "employee_salary_summary" },
        { title: "Payroll Detailed Summary", icon: FileText, description: "Detailed payroll report with components.", route: "/reports/payroll-detailed-summary", permissionKey: "payroll_detailed_summary" },
        { title: "Employee ESI Statement", icon: FileArchive, description: "Employee ESI contributions and statements.", route: "/reports/employee-esi-statement", permissionKey: "employee_esi_statement" },
        { title: "Employee PF Statement", icon: ClipboardList, description: "Employee Provident Fund details.", route: "/reports/employee-pf-statement", permissionKey: "employee_pf_statement" },
        { title: "Muster Roll", icon: File, description: "Daily workforce attendance report.", route: "/reports/muster-roll", permissionKey: "muster_roll" },
        { title: "Employee Credits & Debits", icon: Users, description: "Track employee credits and deductions.", route: "/reports/employee-credits-debits", permissionKey: "employee_earning_deduction" },
        { title: "Attendance Correction", icon: Clock, description: "Manage and approve attendance corrections.", route: "/reports/attendance-correction", permissionKey: "attendance_correction" },
        { title: "Subordinates Report", icon: UserCheck, description: "Reports related to your subordinates.", route: "/reports/subordinates-report", permissionKey: "subordinates" },
        { title: "Roster Report", icon: ListChecks, description: "Employee roster schedule and shifts.", route: "/reports/roster-report", permissionKey: "roster_report" },
        { title: "Leave Permission Report", icon: FileText, description: "Track employee leave approvals.", route: "/reports/leave-permission-report", permissionKey: "leave_permission_report" },
        { title: "Short-Duty Review", icon: Clock, description: "Missing-punch days and their penalties, month-wise.", route: "/reports/short-duty", permissionKey: "short_duty_report" },
        { title: "Fine Report", icon: Clock, description: "Employee-wise fines and deductions with grace usage.", route: "/reports/fine-report", permissionKey: "fine_report" },
        { title: "Document Upload Report", icon: FileCheck, description: "Yes/No matrix of which documents are on file for each employee.", route: "/reports/document-upload-report", permissionKey: "document_upload_report" },
    ];

    // In org-admin Dashboard Overview mode, the Reports page is a curated subset:
    // only these four reports are shown, regardless of the granular permission
    // map (the org admin has full access anyway). Inside a specific branch, the
    // normal permission-based filtering below applies.
    const OVERVIEW_REPORT_KEYS = [
        "branch_wise_activity_report",
        "day_wise_attendance",
        "month_wise_attendance",
        "salary_process_bank",
        "attendance_correction",
    ];

    const orgAdminReports = allReports.filter(
        (report) => !report.orgAdminOnly || isOrgAdminUser()
    );

    // Filter reports based on user permissions
    const reports = isOrgOverview()
        ? orgAdminReports.filter(report => OVERVIEW_REPORT_KEYS.includes(report.permissionKey))
        : orgAdminReports.filter(report => {
            if (report.orgAdminOnly && isOrgAdminUser()) {
                return true;
            }
            if (!reportPermissions || !reportPermissions.subMenu) {
                return false;
            }

            const permission = reportPermissions.subMenu.find(sub => sub.label === report.permissionKey);
            if (permission && permission.permission === true) {
                return true;
            }
            // Reporting managers can open Department-Wise Activity even without a reports permission key.
            if (isReportingManager && report.route === "/reports/department-wise-activity") {
                return true;
            }
            return false;
        });

    const navigate = useNavigate();
    const [view, setView] = useState("card"); // card | list | table
    const [search, setSearch] = useState("");
    const { isMobile } = useSidebar();

    const filteredReports = reports.filter(report =>
        report.title.toLowerCase().includes(search.toLowerCase()) ||
        report.description.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <ContainerCard scrollable>
            <div className="p-2">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">
                            Reports
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Access all your HR and payroll reports in one place
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        {/* Search Bar */}
                        <div className="relative flex-1 max-w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="search"
                                placeholder="Search reports..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className={cn(
                                    "pl-9 pr-3 h-10 w-full rounded-lg",
                                    "bg-background border border-input",
                                    "placeholder:text-muted-foreground",
                                    "transition-colors duration-200"
                                )}
                            />
                        </div>

                        {/* View Toggle */}
                        {!isMobile && (
                            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn(
                                        "p-2 rounded-md transition-all duration-200",
                                        view === "card"
                                            ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                                            : "text-muted-foreground hover:text-foreground hover:bg-accent"
                                    )}
                                    onClick={() => setView("card")}
                                >
                                    <Grid className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn(
                                        "p-2 rounded-md transition-all duration-200",
                                        view === "list"
                                            ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                                            : "text-muted-foreground hover:text-foreground hover:bg-accent"
                                    )}
                                    onClick={() => setView("list")}
                                >
                                    <List className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn(
                                        "p-2 rounded-md transition-all duration-200",
                                        view === "table"
                                            ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                                            : "text-muted-foreground hover:text-foreground hover:bg-accent"
                                    )}
                                    onClick={() => setView("table")}
                                >
                                    <Table className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Reports Count */}
                <div className="mb-4">
                    <p className="text-sm text-muted-foreground">
                        {filteredReports.length} {filteredReports.length === 1 ? 'report' : 'reports'} found
                    </p>
                </div>

                {/* Reports Display */}
                {view === "card" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {filteredReports.map((report, idx) => {
                            const Icon = report.icon;
                            return (
                                <div
                                    key={idx}
                                    onClick={() => navigate(report.route)}
                                    className={cn(
                                        "group flex flex-col gap-3 p-5 rounded-xl border",
                                        "bg-card text-card-foreground border-border",
                                        "hover:shadow-md hover:border-sidebar dark:hover:border-primary",
                                        "cursor-pointer transition-all duration-200",
                                        "hover:scale-[1.02] transform-gpu"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "p-2.5 rounded-lg",
                                            "bg-primary/10  dark:bg-primary text-sidebar",
                                            "group-hover:bg-primary/20 dark:group-hover:bg-primary/40 transition-colors duration-200"
                                        )}>
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-gray-700 group-hover:text-sidebar dark:group-hover:text-sidebar-primary transition-colors duration-200">
                                            {report.title}
                                        </h3>
                                    </div>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        {report.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}

                {view === "list" && (
                    <div className="space-y-2">
                        {filteredReports.map((report, idx) => {
                            const Icon = report.icon;
                            return (
                                <div
                                    key={idx}
                                    onClick={() => navigate(report.route)}
                                    className={cn(
                                        "group flex items-center gap-4 p-4 rounded-lg border",
                                        "bg-card text-card-foreground border-border",
                                        "hover:shadow-sm hover:border-sidebar",
                                        "cursor-pointer transition-all duration-200"
                                    )}
                                >
                                    <div className={cn(
                                        "p-2 rounded-md",
                                        "bg-primary/10 text-sidebar",
                                        "group-hover:bg-primary/20 transition-colors duration-200"
                                    )}>
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-semibold group-hover:text-sidebar transition-colors duration-200 truncate">
                                            {report.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground truncate">
                                            {report.description}
                                        </p>
                                    </div>
                                    <div className={cn(
                                        "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                                        "text-muted-foreground"
                                    )}>
                                        <ChevronRight className="w-4 h-4" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {view === "table" && (
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="w-full bg-card text-card-foreground">
                            <thead className="bg-muted/50">
                                <tr className="text-left text-sm text-muted-foreground">
                                    <th className="px-4 py-3 font-medium border-b border-border">#</th>
                                    <th className="px-4 py-3 font-medium border-b border-border">Report</th>
                                    <th className="px-4 py-3 font-medium border-b border-border">Description</th>
                                    <th className="px-4 py-3 font-medium border-b border-border">Category</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredReports.map((report, idx) => {
                                    const Icon = report.icon;
                                    return (
                                        <tr
                                            key={idx}
                                            onClick={() => navigate(report.route)}
                                            className={cn(
                                                "group cursor-pointer transition-colors duration-200",
                                                "hover:bg-accent/50"
                                            )}
                                        >
                                            <td className="px-4 py-3 text-sm text-muted-foreground border-b border-border">
                                                {idx + 1}
                                            </td>
                                            <td className="px-4 py-3 border-b border-border">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-1.5 rounded-md bg-primary/10 text-sidebar">
                                                        <Icon className="w-4 h-4" />
                                                    </div>
                                                    <span className="text-sm font-medium group-hover:text-sidebar transition-colors duration-200">
                                                        {report.title}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-muted-foreground border-b border-border">
                                                {report.description}
                                            </td>
                                            <td className="px-4 py-3 border-b border-border">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                                    HR
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Empty State */}
                {filteredReports.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="p-4 rounded-full bg-muted mb-4">
                            <Search className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            No reports found
                        </h3>
                        <p className="text-muted-foreground max-w-md">
                            No reports match your search criteria. Try different keywords or browse all reports.
                        </p>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default ReportIndex;